import {action, observable, reaction} from 'mobx';
import {env} from 'next-runtime-env';
import SocketClient from '@utils/socket';
import BaseStore from './baseStore';
import {getGameEventV3, PLAYER_NOTIFICATION_TYPE} from '@constant/game';
import {
  clearLocalStorageExceptDontShowDisclaimerAgain,
  getFailRedirectUrl,
  shouldRedirect,
  showSystemError,
} from '..';
import Router from 'next/router';
import {Modal, notification} from 'antd';
import SystemStore from './system';
import i18next from 'i18next';
import UserStore from './user';
import {disconnect as wagmiDisconnect} from '@wagmi/core';
import WalletConnectConfig from '@configs/WalletConnectConfig';
import {isTelegramMiniApp} from '@components/layout';
import React from 'react';

const REDIRECT_TIMEOUT_MS = 6000;

const NEXT_PUBLIC_WS_URL = env('NEXT_PUBLIC_WS_URL');

interface SocketStore {
  client: SocketClient;
}

class SocketStore extends BaseStore {
  @observable client: SocketClient;

  @observable isConnected: boolean = false;

  @observable isAuthenticated: boolean = false;

  @observable isTokenUpdated: boolean = false;

  system: SystemStore;

  user: UserStore;

  @observable isMaintenanceMode: boolean = false;

  // Track authentication readiness for room operations
  @observable isReadyForRoomOperations: boolean = false;

  constructor(system: SystemStore, user: UserStore) {
    super();
    this.system = system;
    this.user = user;
    this.isReady = true;
    // * isTokenUpdated is true if sessionStorage.getItem('isTokenUpdated') is 'true'
    this.isTokenUpdated = sessionStorage.getItem('isTokenUpdated') === 'true';

    // Wait for localStorage to update when accessing login-with-token
    // only run this when user isn't on telegram
    // * fixing telegram disconnect issue
    // if (typeof window !== 'undefined' && !isTelegramMiniApp) {
    if (typeof window !== 'undefined' && !isTelegramMiniApp) {
      setTimeout(() => this.init(), 2000);
    } else if (isTelegramMiniApp && this.isTokenUpdated) {
      // * if using old token for socket connection
      // * invalid token would occur
      console.log(' isTelegramMiniApp && isTokenUpdated => init() ');
      this.init();
    }

    // * step 3: for page refresh, call init() if isTokenUpdated is true
    // reaction to persist changes to isTokenUpdated in sessionStorage
    if (isTelegramMiniApp) {
      reaction(
        () => this.isTokenUpdated, // track changes to isTokenUpdated
        newValue => {
          console.log(' reaction triggered: isTokenUpdated changed to', newValue);
          sessionStorage.setItem('isTokenUpdated', newValue.toString());
        }
      );
    }
  }

  @action init() {
    console.log(
      'socketStore - init() - localStorage.getItem(playerToken)',
      localStorage.getItem('playerToken')
    );
    this.client = new SocketClient(
      `${NEXT_PUBLIC_WS_URL}/game`,
      localStorage.getItem('playerToken')
    );
3
    const GAME_EVENTS = getGameEventV3();

    const handleRedirectToGatewayError = (message: string, forceRedirect = false) => {
      this.system.hideGameOverlay();
      // TODO @@ double check
      // this.user.updatePlayerToken('');
      // TODO @@ re-direct reason
      if (forceRedirect || shouldRedirect(Router.pathname)) {
        this.system.showModal(message);
        setTimeout(async () => {
          this.system.dismissModal();
          this.system.hideGameOverlay();

          // wallet connect sign out
          await wagmiDisconnect(WalletConnectConfig);
          clearLocalStorageExceptDontShowDisclaimerAgain();
          Router.push({pathname: getFailRedirectUrl(), query: Router.query});
        }, REDIRECT_TIMEOUT_MS);
      }
    };

    const handleLoginSomewhereElse = () => {
      handleRedirectToGatewayError(
        // 'You were logged in somewhere else, you will be redirected to home page after few seconds'
        '你在其他地方被登錄，你將會在數秒後返回主頁面',
        true
      );
    };

    const sameDeviceLogin = () => {
      handleRedirectToGatewayError(
        // 'Detected other account logged in from the same device, you will be redirected to home page after few seconds'
        '偵測到你在相同裝置登錄了第二個帳戶，你將會在數秒後返回主頁面',
        true
      );
    };

    const invalidLogin = () => {
      handleRedirectToGatewayError(
        // 'Invalid login infomation, you will be redirected to home page after few seconds'
        '無效登錄，你將會在數秒後返回主頁面'
      );
    };

    this.client.socket.on(GAME_EVENTS.LOGIN_TOKEN_INVALID, () => {
      console.log('[SocketStore] Login token invalid - authentication failed');
      this.isAuthenticated = false;
      this.isReadyForRoomOperations = false;
      invalidLogin();
    });
    // TODO @@ telegram bug
    this.client.socket.on(GAME_EVENTS.DUPLICATE_LOGIN, () => {
      handleLoginSomewhereElse();
    });
    // this.client.socket.on(
    //   GAME_EVENTS.EM_SERVER_NOTIFICATIONS_NOTIFY_RESTRICTION_IP_DEVICE_SAME_ROOM,
    //   () => {
    //     sameDeviceLogin();
    //   }
    // );
    this.client.socket.on(GAME_EVENTS.EM_SERVER_NOTIFICATIONS_NOTIFY_SERVER_MAINTENANCE, () => {
      this.system.hideGameOverlay();
      this.isMaintenanceMode = true;
      Router.push('/maintenance');
    });
    this.client.socket.on(GAME_EVENTS.DEVICE_LOGIN_RESTRICTION, () => {
      sameDeviceLogin();
    });
    this.client.socket.on('connect', () => {
      console.log('[SocketStore] Socket connected, starting authentication process');
      this.isConnected = true;
      this.isAuthenticated = false; // Reset auth status on new connection
      this.isReadyForRoomOperations = false;
      
      // Authentication happens automatically when socket connects with valid token
      // We'll set isAuthenticated to true after a short delay to ensure auth is complete
      setTimeout(() => {
        console.log('[SocketStore] Socket authentication should be complete, marking as ready');
        this.isAuthenticated = true;
        this.isReadyForRoomOperations = true;
        
        // Emit a custom event that components can listen to
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('socketAuthenticated', {
            detail: { isAuthenticated: true, isReadyForRoomOperations: true }
          }));
        }
      }, 500); // Small delay to ensure server-side authentication is complete
    });
    
    this.client.socket.on('disconnect', () => {
      console.log('[SocketStore] Socket disconnected');
      this.isConnected = false;
      this.isAuthenticated = false;
      this.isReadyForRoomOperations = false;
    });


    // Handle tournament result announcement
    this.client.socket.on(GAME_EVENTS.GAME_ROOM.TOURNAMENT_RESULT_ANNOUNCEMENT, data => {
      console.log('Tournament result announcement (global)', data);
      const rank = data?.ranking || data?.rank;
      const prize = data?.prize;
      
      const infoIcon = React.createElement('img', {
        src: '/icon/notification/info-icon.svg',
        alt: 'info',
        style: { width: '24px', height: '24px' }
      });
      
      const closeIcon = React.createElement('span', {
        style: { color: '#E8EAED' }
      }, '✕');
      
      notification.info({
        message: 'Tournament Ended',
        description: prize 
          ? `Congratulations! You finished in rank ${rank} and won $${prize} in ${data?.tournamentName || 'the tournament'}.`
          : `You finished in rank ${rank} in ${data?.tournamentName || 'the tournament'}.`,
        icon: infoIcon,
        closeIcon: closeIcon,
        duration: 8,
      });
      this.user?.refreshPlayerInfo();
    });

    // Handle wallet and tournament notifications globally
    this.client.socket.on(GAME_EVENTS.WALLET_EVENT_NOTIFICATION, data => {
      console.log('wallet event notification (global)', data);
      const t = i18next.t;
      
      // Create icon and close button elements
      const infoIcon = React.createElement('img', {
        src: '/icon/notification/info-icon.svg',
        alt: 'info',
        style: { width: '24px', height: '24px' }
      });
      
      const closeIcon = React.createElement('span', {
        style: { color: '#E8EAED' }
      }, '✕');
      
      // Handle all notifications by notificationType
      if (data?.notificationType === 'deposit') {
        const amount = parseFloat(data.amount || data.transactionAmount || '0').toFixed(2);
        const currency = data.currencyId || 'USD';
        const status = data.status?.toUpperCase();
        
        if (status === 'PENDING') {
          notification.info({
            message: 'Deposit Pending',
            description: `Your deposit of ${currency} ${amount} is being processed.`,
            icon: infoIcon,
            closeIcon: closeIcon,
            duration: 8,
          });
        } else if (status === 'COMPLETED' || status === 'CONFIRMED' || status === 'SUCCESS') {
          notification.info({
            message: 'Deposit Approved',
            description: `Your deposit of ${currency} ${amount} has been approved successfully.`,
            icon: infoIcon,
            closeIcon: closeIcon,
            duration: 8,
          });
        }
        this.user?.refreshPlayerInfo();
      } else if (data?.notificationType === 'withdrawal') {
        const amount = parseFloat(data.amount || data.transactionAmount || '0').toFixed(2);
        const currency = data.currencyId || 'USD';
        const status = data.status?.toUpperCase();
        
        if (status === 'PENDING') {
          notification.info({
            message: 'Withdrawal Pending',
            description: `Your withdrawal of ${currency} ${amount} is being processed.`,
            icon: infoIcon,
            closeIcon: closeIcon,
            duration: 8,
          });
        } else if (status === 'COMPLETED' || status === 'CONFIRMED' || status === 'SUCCESS') {
          notification.info({
            message: 'Withdrawal Approved',
            description: `Your withdrawal of ${currency} ${amount} has been approved successfully.`,
            icon: infoIcon,
            closeIcon: closeIcon,
            duration: 8,
          });
        }
        this.user?.refreshPlayerInfo();
      } else if (data?.notificationType === 'tournament_cancelled') {
        notification.info({
          message: 'Tournament Cancelled',
          description: `${data.tournamentName || 'TOURNAMENT NAME'} has been cancelled.`,
          icon: infoIcon,
          closeIcon: closeIcon,
          duration: 8,
        });
        this.user?.refreshPlayerInfo();
      } else if (data?.notificationType === 'tournament_entry_fee_refund') {
        notification.info({
          message: 'Entry Fee has been refunded',
          description: `${data.tournamentName || 'TOURNAMENT NAME'} has been cancelled. ${data.refundAmount ? `$${data.refundAmount}` : '$100'} entry fee has been refunded to your account successfully.`,
          icon: infoIcon,
          closeIcon: closeIcon,
          duration: 8,
        });
        this.user?.refreshPlayerInfo();
      } else if (data?.notificationType === 'tournament_end') {
        const rank = data.ranking || data.rank;
        const prize = data.prize;
        notification.info({
          message: 'Tournament Ended',
          description: prize 
            ? `Congratulations! You finished in rank ${rank} and won $${prize} in ${data.tournamentName || 'the tournament'}.`
            : `You finished in rank ${rank} in ${data.tournamentName || 'the tournament'}.`,
          icon: infoIcon,
          closeIcon: closeIcon,
          duration: 8,
        });
        this.user?.refreshPlayerInfo();
      }
    });
  }

  @action async call(...props: Parameters<typeof SocketClient.call>) {
    return this.client?.call(...props);
  }

  // @action async subscribe(...props: Parameters<Subscribe>) {
  //   return this.client.subscribe(...props);
  // }

  @action async disconnect() {
    this.isConnected = false;
    return this.client?.disconnect();
  }

  @action async connect() {
    console.log('socketStore - connect()');
    // Establish new object so that token can be update-to-date
    await this.disconnect();
    this.init();

    return this.client?.connect();
  }

  @action markTokenAsUpdated() {
    // * step 2: token is updated, call init()
    console.log('markTokenAsUpdated => init()');
    this.isTokenUpdated = true;
    this.init();
  }

  // @action async testRes(...props) {
  //   return this.client.testRes(...props);
  // }
}

export default SocketStore;
